import { useEffect, useMemo, useState } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import { parseAndValidateAddedCourses } from '../SelectAdditionalCourses/utils/parseAddedCourses'
import {
  isGroupPerLessonLinkTariff,
  isLessonPriceLinkTariff,
} from '../../tariffTypes'
import {
  getIndividualLessonLinkPackageTotal,
  getIndividualLessonLinkTotal,
} from './individualLessonLink.utils'

type UseIndividualLessonLinkFormParams = {
  form: FormInstance
  typeWatch: string | null | undefined
  selectedTariff?: ICourseTariff | null
  valueWatch: number | null
  firstPriceWatch: number | null
  /** Текущее form.price основного курса (после ручной правки / синка). */
  priceWatch?: number | null
  /** Итого editable только при фиксации кол-ва занятий. */
  isValueFixedWatch?: boolean | null
  discountWatch?: number | null
  additionalCoursesWatch: string | null
  courses?: readonly { id?: number | null }[] | null
  extraCoursesDiscountsRaw?: string | null
  extraCoursesDiscountUnit?: string | null
}

/**
 * Оркестрация hour / minute / group_per_lesson в create-link:
 * form.price = только основной курс; packageTotal = основной + допы.
 */
export const useIndividualLessonLinkForm = ({
  form,
  typeWatch,
  selectedTariff,
  valueWatch,
  firstPriceWatch,
  priceWatch = null,
  isValueFixedWatch = false,
  discountWatch = null,
  additionalCoursesWatch,
  courses,
  extraCoursesDiscountsRaw,
  extraCoursesDiscountUnit,
}: UseIndividualLessonLinkFormParams) => {
  const [isPriceManuallyEdited, setIsPriceManuallyEdited] = useState(false)

  const isActive =
    typeWatch === 'course' && isLessonPriceLinkTariff(selectedTariff?.type)
  const isGroupPerLesson = isGroupPerLessonLinkTariff(selectedTariff?.type)
  const isValueFixed = Boolean(isValueFixedWatch)
  /** Без фиксации кол-ва итого основного всегда расчётное. */
  const isPriceEditable = isValueFixed
  /** XOR: скидка только когда итого locked (gpl без фиксации). */
  const isDiscountEditable = isGroupPerLesson && !isValueFixed
  const discountValue = Math.max(0, Math.trunc(Number(discountWatch) || 0))

  const lessonCountForPrice =
    valueWatch != null && Number(valueWatch) >= 1
      ? Math.trunc(Number(valueWatch))
      : 1

  const mainGrossPrice = useMemo(() => {
    if (!isActive) return 0
    return getIndividualLessonLinkTotal(firstPriceWatch, lessonCountForPrice)
  }, [firstPriceWatch, isActive, lessonCountForPrice])

  /** Расчётная цена только основного курса (со скидкой gpl, если есть). */
  const suggestedPrice = useMemo(() => {
    if (!isActive) return 0
    if (isDiscountEditable && discountValue > 0) {
      return Math.max(0, mainGrossPrice - discountValue)
    }
    return mainGrossPrice
  }, [discountValue, isActive, isDiscountEditable, mainGrossPrice])

  const additionalCoursesMeta = useMemo(() => {
    if (!isActive || !selectedTariff) {
      return { price: 0, count: 0 }
    }

    const additionalCourses = parseAndValidateAddedCourses(
      additionalCoursesWatch ?? null,
    ).map((item) => ({
      ...item,
      course:
        (courses?.find((course) => course.id === item.courseId) as
          | ICourse
          | undefined) ?? null,
      withDemo: false,
    }))

    let extraCoursesDiscounts: number[] = []
    try {
      extraCoursesDiscounts =
        JSON.parse(extraCoursesDiscountsRaw ?? 'null') ?? []
    } catch (error) {}

    const packageGross = getIndividualLessonLinkPackageTotal({
      tariff: selectedTariff,
      firstPrice: firstPriceWatch,
      value: lessonCountForPrice,
      additionalCourses,
      extraCoursesDiscounts,
      extraCoursesDiscountUnit: extraCoursesDiscountUnit ?? 'percent',
    })

    return {
      price: Math.max(0, Number((packageGross - mainGrossPrice).toFixed(2))),
      count: additionalCourses.length,
    }
  }, [
    additionalCoursesWatch,
    courses,
    extraCoursesDiscountUnit,
    extraCoursesDiscountsRaw,
    firstPriceWatch,
    isActive,
    lessonCountForPrice,
    mainGrossPrice,
    selectedTariff,
  ])

  const additionalCoursesPrice = additionalCoursesMeta.price
  const additionalCoursesCount = additionalCoursesMeta.count

  const mainPriceForPackage = useMemo(() => {
    if (!isActive) return 0
    if (isPriceManuallyEdited && priceWatch != null) {
      return Math.max(0, Number(priceWatch) || 0)
    }
    return suggestedPrice
  }, [isActive, isPriceManuallyEdited, priceWatch, suggestedPrice])

  const packageTotal = useMemo(
    () =>
      Number((mainPriceForPackage + additionalCoursesPrice).toFixed(2)),
    [additionalCoursesPrice, mainPriceForPackage],
  )

  useEffect(() => {
    if (!isActive) return
    // При editable-итоге скидку не держим (режим xor с gpl).
    if (isPriceEditable && (discountWatch != null || discountValue > 0)) {
      form.setFieldsValue({
        discount: null,
        apply_discount_each_payment: false,
      })
    }
  }, [discountValue, discountWatch, form, isActive, isPriceEditable])

  useEffect(() => {
    if (!isActive) return
    if (!isPriceEditable) {
      setIsPriceManuallyEdited(false)
      form.setFieldValue('price', suggestedPrice)
      return
    }
    if (isPriceManuallyEdited) return
    form.setFieldValue('price', suggestedPrice)
  }, [
    form,
    isActive,
    isPriceEditable,
    isPriceManuallyEdited,
    suggestedPrice,
  ])

  const onPriceManualChange = () => {
    if (!isPriceEditable) return
    setIsPriceManuallyEdited(true)
  }

  const onResetSuggestedPrice = () => {
    setIsPriceManuallyEdited(false)
    form.setFieldValue('price', suggestedPrice)
  }

  return {
    isActive,
    isPriceEditable,
    isDiscountEditable,
    /** Итог пакета (основной + допы) — для отображения внизу. */
    totalPrice: packageTotal,
    packageTotal,
    mainPrice: mainPriceForPackage,
    additionalCoursesPrice,
    additionalCoursesCount,
    /** Расчётная цена основного курса. */
    suggestedPrice,
    lessonCountForPrice,
    isPriceManuallyEdited,
    onPriceManualChange,
    onResetSuggestedPrice,
    setIsPriceManuallyEdited,
  }
}
